import type { MouseEventHandler } from "react";
import { useCallback, useEffect, useState } from "react";
import { cx } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { Player } from "~/components/Player";
import { usePlayerControls } from "~/hooks/player/usePlayerControls";
import { useContents } from "~/hooks/useContents";
import {
  PlayerType,
  useContentDataStore,
  usePlaybackStore,
} from "~/stores/playerStore";
import { checkMobile } from "~/utils/checkMobile";
import { checkPlayability } from "~/utils/checkPlayablilty";
import { gtag } from "~/utils/gtag";
import { Controls } from "./Controls";
import styles from "./DefaultPlayer.module.css";
import { ToastPortal } from "../ToastPortal";

interface DefaultPlayerProps {
  controlsVisible: boolean;
  controlsVisibleTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  restartControlsVisibleTimeout: () => void;
  onDefaultScreenClick: () => void;
  onEnded: () => void;
  contentCode: string;
  stillcut: string;
  replay?: boolean;
  onReplay?: () => void;
  onShowGuide?: () => void;
  isLastEpisode: boolean;
  className?: string;
  id?: string;
  liked: boolean;
  likesCount: string;
  onLike: () => void;
  lastScrollClicked?: boolean;
  onSetLastScrollClicked?: (value: boolean) => void;
}

export function DefaultPlayer({
  controlsVisible,
  controlsVisibleTimeoutRef,
  restartControlsVisibleTimeout,
  onDefaultScreenClick,
  onEnded,
  replay,
  onReplay,
  onShowGuide,
  isLastEpisode,
  contentCode,
  stillcut,
  className,
  id,
  liked,
  likesCount,
  onLike,
  lastScrollClicked = false,
  onSetLastScrollClicked,
}: DefaultPlayerProps) {
  const { t } = useTranslation("common");
  const isPlayable = useContentDataStore(state =>
    checkPlayability("full", state.data),
  );
  const [showToast, setShowToast] = useState(false);

  const { data: contentData } = useContents({ id: contentCode });

  const {
    refs: { playerElementRef },
    playerControls,
  } = usePlayerControls();

  const handlePlayerReady = useCallback(async () => {
    if (isPlayable) {
      try {
        await playerControls.current.onPlay({});
        gtag.sendEvent({
          eventName: "w_play",
          wEventId: 2,
          screenName: "player",
          wSeasonCode: contentData?.result.seasonId ?? "",
          wEpisodeNumber: contentData?.result.number ?? 0,
          wIsFree: true,
        });
      } catch (e: any) {
        if (e.name !== "NotAllowedError") {
          throw e;
        }
      }
    }
  }, [playerControls, isPlayable]);

  const handlePlayerError = useCallback(() => {
    playerControls.current.onPause();
  }, []);

  const handlePauseToggle: MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      if (usePlaybackStore.getState().paused) {
        restartControlsVisibleTimeout();
      } else {
        clearTimeout(controlsVisibleTimeoutRef.current!);
      }

      playerControls.current.onPauseToggle();
    },
    [playerControls, restartControlsVisibleTimeout],
  );

  async function handleCopyLink(e: any) {
    e.stopPropagation();
    e.preventDefault();
    if (checkMobile()) {
      try {
        await navigator.share({
          title: "숏챠",
          text: contentData?.result.title,
          url: contentData?.result.link,
        });
        gtag.sendEvent({
          eventName: "w_action",
          wEventId: 12,
          screenName: "player",
          wActionType: "share",
          wSeasonCode: contentData?.result.seasonId ?? "",
        });
      } catch (err: any) {
        console.error(err);
      }
    } else {
      navigator.clipboard.writeText(contentData?.result.link ?? "").then(
        () => {
          gtag.sendEvent({
            eventName: "w_action",
            wEventId: 12,
            screenName: "player",
            wActionType: "share",
            wSeasonCode: contentData?.result.seasonId ?? "",
          });
          setShowToast(true);
        },
        () => {
          setShowToast(true);
        },
      );
    }
  }

  useEffect(() => {
    if (replay) {
      playerControls.current.onPlay();
      onReplay?.();
    }
  }, [replay, onReplay]);

  useEffect(() => {
    if (isLastEpisode) {
      const snapScroll = document.getElementById("snapScroll");

      if (snapScroll) {
        snapScroll.onscrollend = () => {
          setTimeout(() => {
            snapScroll.onwheel = e => {
              if (e.deltaY > 0) {
                playerControls.current.onPause();
                onShowGuide?.();
              }
            };
          }, 500);
        };
      }
    }

    return () => {
      const snapScroll = document.getElementById("snapScroll");

      if (snapScroll) {
        snapScroll.onscrollend = null;
        snapScroll.onwheel = null;
      }
    };
  }, []);

  useEffect(() => {
    if (lastScrollClicked && onSetLastScrollClicked) {
      onSetLastScrollClicked(false);
      playerControls.current.onPause();
    }
  }, [lastScrollClicked]);

  return (
    <div
      className={cx(className, styles.wrapper)}
      id={id}
      onMouseUp={onDefaultScreenClick}
    >
      {showToast ? (
        <ToastPortal
          toasts={[{ key: id ?? "copy", text: t("copyCompleted") }]}
          onToastClose={() => setShowToast(false)}
        />
      ) : null}
      <Player
        ref={playerElementRef}
        contentCode={contentCode}
        playerType={PlayerType.FULL}
        stillcut={stillcut}
        onEnded={onEnded}
        onError={handlePlayerError}
        onPlaying={() => {}}
        onReady={handlePlayerReady}
      >
        <Controls
          controlsVisibleTimeoutRef={controlsVisibleTimeoutRef}
          liked={liked}
          likesCount={likesCount}
          visible={controlsVisible}
          onDefaultScreenClick={onDefaultScreenClick}
          onLike={onLike}
          onPause={playerControls.current.onPause}
          onPauseToggle={handlePauseToggle}
          onPlay={playerControls.current.onPlay}
          onSeekByProgress={playerControls.current.onSeekByProgress}
          onShare={handleCopyLink}
        />
      </Player>
    </div>
  );
}
